import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import useLivePolling from "../hooks/useLivePolling";

const NotifCtx = createContext(null);
export const useLiveNotifications = () => useContext(NotifCtx);

// iter: Bloque 2 — conservative first step, 3s -> 10s. Both pollers already
// pause while the tab is hidden and resume on focus via useLivePolling.
const POLL_INTERVAL_MS = 10000;
const TOAST_TTL_MS = 7000;
const MAX_TOASTS = 5;

// iOS-style "tri-ton" notification (3 quick rising bells, like the
// classic Apple notification sound). Generated with Web Audio API — no asset.
let _audio = null;
const playSound = () => {
  try {
    if (!_audio) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const playBell = (freq, when, dur = 0.18, vol = 0.10) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g);
        g.connect(ctx.destination);
        o.type = "sine";
        o.frequency.setValueAtTime(freq, ctx.currentTime + when);
        g.gain.setValueAtTime(0.0001, ctx.currentTime + when);
        g.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + when + 0.015);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + when + dur);
        o.start(ctx.currentTime + when);
        o.stop(ctx.currentTime + when + dur + 0.02);
      };
      _audio = () => {
        // Classic iOS "tri-tone": three rising notes (G5, B5, D6)
        playBell(784, 0);       // G5
        playBell(988, 0.10);    // B5
        playBell(1175, 0.20);   // D6
      };
    }
    _audio();
  } catch (e) { /* silent */ }
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [toasts, setToasts] = useState([]); // [{id, title, message, link, type, created_at, priority, kind?: "chat"|"notif"}]
  const lastTsRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);
  const isMountedRef = useRef(true);

  // Compute current route scope. Notifications are an ADMINISTRATIVE tool —
  // they must NEVER appear on:
  //   • Public routes (/b/...)   → clients/visitors must not see internal alerts
  //   • Tenant portals (/portal/..., /pro/...) → each tenant has its OWN
  //     in-portal alerts (dashboard alerts inside the portal). /pro/ is the
  //     Hanoy professional/secretary portal — same tenant-isolation reasoning
  //     as /portal/ (the owner's portal), just a different route prefix.
  //     This is defense-in-depth: the backend (_notif_visibility_query) is
  //     the actual authority and already denies these roles outright, but a
  //     poller must not even be mounted here for a tenant-portal route.
  // They ONLY appear on the main Commandix CRM (everything else).
  const path = location?.pathname || "";
  const isPublicRoute = (
    path.startsWith("/b/") ||
    path.startsWith("/login") ||
    path === "/" ||
    path.startsWith("/signup") ||
    path.startsWith("/register") ||
    path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password")
  );
  const isTenantPortal = path.startsWith("/portal/") || path.startsWith("/pro/");
  const suppressGlobalToasts = isPublicRoute || isTenantPortal;

  const pushToast = useCallback((notif) => {
    setToasts((prev) => {
      // dedup by id
      if (prev.find((p) => p.id === notif.id)) return prev;
      const next = [notif, ...prev].slice(0, MAX_TOASTS);
      return next;
    });
    // auto-dismiss
    setTimeout(() => {
      if (!isMountedRef.current) return;
      setToasts((prev) => prev.filter((t) => t.id !== notif.id));
    }, TOAST_TTL_MS);
  }, []);

  // Clear any lingering toasts when navigating into a tenant portal or a
  // public page, so internal CRM alerts don't leak across scopes. The
  // pollers themselves are mounted/unmounted below based on this same
  // condition (see NotifCtx.Provider render) — no timer exists at all
  // while suppressed or logged out.
  useEffect(() => {
    if (suppressGlobalToasts) setToasts([]);
  }, [suppressGlobalToasts]);

  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false; }; }, []);

  const pollersActive = !!user && !suppressGlobalToasts;

  // Service worker + web push subscription (once per session)
  useEffect(() => {
    if (!user) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    let cancelled = false;
    const setup = async () => {
      try {
        const reg = await navigator.serviceWorker.register("/sw-push.js");
        // listen for navigation messages from SW
        navigator.serviceWorker.addEventListener("message", (event) => {
          if (event.data?.type === "navigate" && event.data.url) navigate(event.data.url);
        });
        // Check current permission state
        if (Notification.permission !== "granted") return; // user must opt-in via button
        // Already granted → ensure subscription is up to date
        await subscribeForPush(reg);
      } catch (e) {
        if (!cancelled) console.warn("[push] sw error", e);
      }
    };
    setup();
    return () => { cancelled = true; };
  }, [user, navigate]);

  const subscribeForPush = useCallback(async (regParam) => {
    try {
      const reg = regParam || await navigator.serviceWorker.ready;
      const cfg = await api.get("/notifications/push-config");
      const pub = cfg.data?.vapid_public_key;
      if (!pub) return false;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(pub),
        });
      }
      const subJson = sub.toJSON();
      await api.post("/notifications/push-subscribe", {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        user_agent: navigator.userAgent,
      });
      return true;
    } catch (e) {
      console.warn("[push] subscribe error", e);
      return false;
    }
  }, []);

  const enablePush = useCallback(async () => {
    if (!("Notification" in window)) return { ok: false, reason: "unsupported" };
    if (Notification.permission === "denied") return { ok: false, reason: "denied" };
    const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
    if (perm !== "granted") return { ok: false, reason: "denied" };
    const reg = await navigator.serviceWorker.register("/sw-push.js");
    const ok = await subscribeForPush(reg);
    return { ok, reason: ok ? "subscribed" : "vapid_missing" };
  }, [subscribeForPush]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const open = useCallback((notif) => {
    dismiss(notif.id);
    if (notif.link) navigate(notif.link);
    // best-effort: mark as read on backend (only for real notifications,
    // chat messages have their own read tracking inside ChatPage)
    if (notif.kind !== "chat") {
      try { api.put(`/notifications/${notif.id}/read`).catch(() => {}); } catch { /* */ }
    }
  }, [dismiss, navigate]);

  return (
    <NotifCtx.Provider value={{ enablePush, pushToast, dismiss }}>
      {children}
      {/* Pollers only mount (and only then create a timer) while there is a
          logged-in user on a non-suppressed route — matches the historical
          "if (!user) return" / "if (suppressGlobalToasts) return" guards
          that used to sit before setInterval in this file. */}
      {pollersActive && (
        <>
          <NotificationsSincePoller pushToast={pushToast} lastTsRef={lastTsRef} />
        </>
      )}
      <ToastStack toasts={toasts} onOpen={open} onDismiss={dismiss} />
    </NotifCtx.Provider>
  );
};

// Polls GET /notifications/since for new bell notifications. Mounted only
// while pollersActive (see NotificationProvider render) so no timer exists
// at all when logged out or on a suppressed (public/portal) route.
const NotificationsSincePoller = ({ pushToast, lastTsRef }) => {
  const loadFn = useCallback(async (isCancelled) => {
    const params = lastTsRef.current ? `?ts=${encodeURIComponent(lastTsRef.current)}` : "";
    const r = await api.get(`/notifications/since${params}`);
    // This poller unmounts (see `pollersActive` in NotificationProvider)
    // whenever the user logs out or navigates into a suppressed route —
    // exactly the scopes that must NEVER show an admin toast. A response
    // that resolves after that must not push/play/advance the cursor.
    if (isCancelled()) return;
    const { server_now, items } = r.data || {};
    if (Array.isArray(items) && items.length > 0) {
      for (const n of items) pushToast(n);
      playSound();
    }
    if (server_now) lastTsRef.current = server_now;
  }, [pushToast, lastTsRef]);
  useLivePolling(loadFn, POLL_INTERVAL_MS, [loadFn], { skipInitial: false });
  return null;
};

// (El sondeo del chat interno viejo, GET /chat/recent-incoming, se retiró de la interfaz: el módulo Chat ahora es Telegram y avisa desde su propia burbuja.)

// ----- Apple-style toast stack -----
const TYPE_ICON = {
  hanoy_payment_proof: "💰",
  hanoy_overflow_request: "📅",
  out_for_delivery: "🚚",
  order_delivered: "📦",
  exception: "⚠️",
  label_request: "🏷️",
  billing_overdue: "💳",
  pending_approval: "🔔",
  chat_message: "💬",
  default: "🔔",
};

const ToastStack = ({ toasts, onOpen, onDismiss }) => {
  if (toasts.length === 0) return null;
  return (
    <div
      data-testid="notif-toast-stack"
      className="fixed z-[9999] top-3 left-1/2 -translate-x-1/2 sm:top-4 sm:left-auto sm:right-4 sm:translate-x-0 flex flex-col gap-2 w-[92vw] max-w-[380px] pointer-events-none"
    >
      {toasts.map((t, idx) => (
        <ToastCard key={t.id} notif={t} index={idx} onOpen={onOpen} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

const ToastCard = ({ notif, index, onOpen, onDismiss }) => {
  const [entering, setEntering] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setEntering(false), 16);
    return () => clearTimeout(t);
  }, []);
  const icon = TYPE_ICON[notif.type] || TYPE_ICON.default;
  const ago = relativeTime(notif.created_at);
  const isHigh = notif.priority === "high";
  const isChat = notif.kind === "chat";
  const iconGradient = isChat
    ? "bg-gradient-to-br from-sky-500 to-blue-600"
    : isHigh
      ? "bg-gradient-to-br from-orange-500 to-red-600"
      : "bg-gradient-to-br from-zinc-700 to-zinc-800";
  const borderColor = isChat
    ? "border-sky-500/40"
    : isHigh
      ? "border-orange-500/40"
      : "border-white/10";
  const eyebrow = isChat ? "Mensaje" : "Commandix";
  return (
    <div
      data-testid={`notif-toast-${notif.id}`}
      onClick={() => onOpen(notif)}
      style={{
        transform: entering ? "translateY(-24px)" : `translateY(0px) scale(${1 - index * 0.015})`,
        opacity: entering ? 0 : 1,
        transition: "transform 280ms cubic-bezier(.2,.9,.2,1.1), opacity 240ms",
      }}
      className={`pointer-events-auto cursor-pointer select-none rounded-2xl
        bg-zinc-900/85 dark:bg-zinc-900/85 backdrop-blur-2xl
        border ${borderColor}
        shadow-2xl shadow-black/60
        hover:bg-zinc-900/95 transition-colors
        px-3.5 py-3`}
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-xl ${iconGradient}`}>
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className="text-[11px] uppercase tracking-wider font-bold text-white/60">{eyebrow}</span>
            <span className="text-[10px] text-white/40">{ago}</span>
          </div>
          <h4 className="text-[13.5px] font-semibold text-white leading-tight truncate">{notif.title || "Notificación"}</h4>
          <p className="text-[12px] text-white/75 leading-snug mt-0.5 line-clamp-2 whitespace-pre-wrap break-words">{notif.message || ""}</p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onDismiss(notif.id); }}
          className="shrink-0 -mr-1 -mt-1 w-6 h-6 rounded-full flex items-center justify-center text-white/40 hover:text-white/90 hover:bg-white/10 transition"
          aria-label="Cerrar"
          data-testid={`notif-dismiss-${notif.id}`}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

function relativeTime(iso) {
  if (!iso) return "ahora";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "ahora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch { return ""; }
}
